import { useCallback, useRef } from 'react'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'

type ActionType = 'view' | 'explore' | 'strengthen' | 'decay'

const STRENGTH_DELTAS: Record<ActionType, number> = {
  view: 0.02,
  explore: 0.10,
  strengthen: 0.15,
  decay: -0.10
}

const DEBOUNCE_MS = 1000

export function useSynapseStrength() {
  const pendingUpdates = useRef<Map<string, { action: ActionType; timer: ReturnType<typeof setTimeout> }>>(new Map())

  const updateStrength = useCallback(async (nodeId: string, action: ActionType) => {
    if (!isSupabaseConfigured() || !nodeId) return

    // Cancel any pending update for this node
    const existing = pendingUpdates.current.get(nodeId)
    if (existing) {
      clearTimeout(existing.timer)
    }

    // Debounce the update
    const timer = setTimeout(async () => {
      try {
        const delta = STRENGTH_DELTAS[action]

        // Try RPC first (strengthen_synapse function)
        const { error: rpcError } = await supabase.rpc('strengthen_synapse', {
          p_node_id: nodeId,
          p_node_type: 'concept',
          p_action: action
        })

        if (rpcError) {
          // Fallback: direct update on concepts table
          const { data: current } = await supabase
            .from('concepts')
            .select('synapse_strength, access_count')
            .eq('id', nodeId)
            .single()

          if (current) {
            const newStrength = Math.max(0, Math.min(1, current.synapse_strength + delta))
            await supabase
              .from('concepts')
              .update({
                synapse_strength: newStrength,
                access_count: current.access_count + (action !== 'decay' ? 1 : 0),
                last_accessed_at: new Date().toISOString()
              })
              .eq('id', nodeId)
          }
        }
      } catch {
        // silent fallback
      } finally {
        pendingUpdates.current.delete(nodeId)
      }
    }, DEBOUNCE_MS)

    pendingUpdates.current.set(nodeId, { action, timer })
  }, [])

  const onView = useCallback((nodeId: string) => updateStrength(nodeId, 'view'), [updateStrength])
  const onExplore = useCallback((nodeId: string) => updateStrength(nodeId, 'explore'), [updateStrength])
  const onStrengthen = useCallback((nodeId: string) => updateStrength(nodeId, 'strengthen'), [updateStrength])
  const onDecay = useCallback((nodeId: string) => updateStrength(nodeId, 'decay'), [updateStrength])

  return { onView, onExplore, onStrengthen, onDecay, updateStrength }
}
